import autotask_icon from "../assets/img/autotask_icon.png";
import datto_icon from "../assets/img/datto_icon.png";
import superoffice_icon from "../assets/img/superops_icon.png";
import m365_icon from "../assets/img/m365_icon.png";
import google_icon from "../assets/img/google_icon.png";
import threatlocker_icon from "../assets/img/threatlocker_icon.png";
import zoom_icon from "../assets/img/zoom_icon.png";
import zapier_icon from "../assets/img/zapier_icon.png";
import slack_icon from "../assets/img/slack_logo.webp";
import sherweb_icon from "../assets/img/sherweb_logo.svg";
import { slugify } from "./stringUtils";

export interface Connector {
  logo: string;
  name: string;
  description: string;
}

export const connectors: Connector[] = [
  {
    logo: autotask_icon,
    name: "Autotask",
    description: "Autotask is a CRM software that helps businesses manage their customer relationships and sales processes.",
  },
  {
    logo: datto_icon,
    name: "Datto",
    description: "Datto is a backup and recovery software that helps businesses protect their data.",
  },
  {
    logo: superoffice_icon,
    name: "SuperOffice",
    description: "SuperOffice is a CRM software that helps businesses manage their customer relationships and sales processes.",
  },
  {
    logo: m365_icon,
    name: "Microsoft 365",
    description: "Microsoft 365 is a cloud-based productivity suite that helps businesses manage their email, calendar, and documents.",
  },
  {
    logo: google_icon,
    name: "Google",
    description: "Google is a search engine that helps businesses manage their email, calendar, and documents.",
  },
  {
    logo: threatlocker_icon,
    name: "ThreatLocker",
    description: "ThreatLocker is a security software that helps businesses protect their data.",
  },
  {
    logo: zoom_icon,
    name: "Zoom",
    description: "Zoom is a video conferencing software that helps businesses manage their video conferencing.",
  },
  {
    logo: zapier_icon,
    name: "Zapier",
    description: "Zapier is a workflow automation software that helps businesses automate their workflows.",
  },
  {
    logo: slack_icon,
    name: "Slack",
    description: "Slack is a team communication software that helps businesses communicate with their team.",
  },
  {
    logo: sherweb_icon,
    name: "Sherweb",
    description: "Sherweb marketplace integration for billing, customers, orders, and subscriptions.",
  },
]; 

export interface ConfigField {
  key: string;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  /** For type "file", comma-separated accept values (e.g. ".pfx") */
  accept?: string;
  /** How uploaded file contents should be stored in form state. */
  fileReadMode?: "base64" | "text";
  /** Optional upload button label for type "file". */
  uploadLabel?: string;
  /** Optional label shown when a file-backed value is already present. */
  configuredLabel?: string;
}

/** Default M365 config for prefill */
export const m365DefaultConfig: Record<string, string> = {
  client_id: "",
  tenant_id: "",
  client_secret: "",
};

/** IT Glue public API host — prefill Base URL in Add Integration, onboarding, and config when empty. */
export const IT_GLUE_DEFAULT_BASE_URL = "https://api.itglue.com";

export const itglueDefaultConfig: Record<string, string> = {
  base_url: IT_GLUE_DEFAULT_BASE_URL,
  api_key: "",
};

export const SHERWEB_DEFAULT_GATEWAY_BASE_URL = "https://api.sherweb.com";

export const sherwebDefaultConfig: Record<string, string> = {
  gateway_base_url: SHERWEB_DEFAULT_GATEWAY_BASE_URL,
  client_id: "",
  client_secret: "",
  subscription_key: "",
};

/** Microsoft Entra admin center URL */
export const ENTRA_ADMIN_CENTER_URL = "https://entra.microsoft.com/";

/** Step content: plain string, object with link, heading (no bullet), numbered (1, 2, 3), or copyable bullets */
export type M365StepContent =
  | string
  | { before: string; linkText: string; linkUrl: string; after: string }
  | { heading: string }
  | { numbered: string }
  | { copyableBullets: string[] };

/** Step-by-step instructions for obtaining M365 credentials (tenant ID, client ID, client secret) */
export const m365SetupInstructions: { step: number; title: string; steps: M365StepContent[] }[] = [
  {
    step: 1,
    title: "Register the application",
    steps: [
      {
        before: "Log in to the ",
        linkText: "Microsoft Entra admin center",
        linkUrl: ENTRA_ADMIN_CENTER_URL,
        after: " (or Azure Portal).",
      },
      "Go to Applications > App registrations > New registration.",
      "Name: Give it a clear name (e.g., \"Theo AI\").",
      "Supported account types: Select \"Accounts in this organizational directory only\" (unless building a multi-tenant app).",
      "Click Register.",
    ],
  },
  {
    step: 2,
    title: "Get Tenant ID & Client ID",
    steps: [
      "On the Overview page after registration:",
      "Tenant ID — copy the value labeled Directory (tenant) ID.",
      "Client ID — copy the value labeled Application (client) ID.",
    ],
  },
  {
    step: 3,
    title: "Generate the Client Secret",
    steps: [
      "In the left sidebar, click Certificates & secrets.",
      "Go to the Client secrets tab and click + New client secret.",
      "Add a description and set an expiration (e.g., 180 days).",
      "Important: Once you click Add, copy the Value immediately. You will not be able to see it again after you leave this screen. This is your Client Secret.",
    ],
  },
  {
    step: 4,
    title: "Upload the Certificate (sent to you by Theo team)",
    steps: [
      "Make sure the Certificates tab is selected (it usually is by default).",
      "Click the Upload certificate button.",
      "Click the folder icon to browse your computer and select your .cer file.",
      "Add a Description (e.g., \"Theo AI Cert\") so you know what it is later.",
      "Click Add.",
    ],
  },
  {
    step: 5,
    title: "Grant API permissions",
    steps: [
      { heading: "To enable all M365 actions, add these required permissions and grant admin consent:" },
      { numbered: "Go to App registrations > your app > API permissions > + Add a permission." },
      { numbered: "Choose Microsoft Graph > Application permissions, then add:" },
      {
        copyableBullets: [
          "AppRoleAssignment.ReadWrite.All",
          "RoleManagement.ReadWrite.Directory",
          "Application.ReadWrite.All",
        ],
      },
      { numbered: "Click \"Grant admin consent\" at the top." },
    ],
  },
];

/** OAuth scopes for Google Workspace domain-wide delegation (Step 2 in setup guide). */
export const gworkspaceOAuthScopes: string[] = [
  "https://www.googleapis.com/auth/admin.directory.user",
  "https://www.googleapis.com/auth/admin.directory.group",
  "https://www.googleapis.com/auth/admin.directory.orgunit",
  "https://www.googleapis.com/auth/gmail.settings.sharing",
  "https://www.googleapis.com/auth/calendar",
  "https://www.googleapis.com/auth/apps.licensing",
  "https://www.googleapis.com/auth/admin.datatransfer",
  "https://www.googleapis.com/auth/admin.directory.user.security",
  "https://www.googleapis.com/auth/admin.directory.rolemanagement",
  "https://www.googleapis.com/auth/admin.directory.rolemanagement.readonly",
  "https://www.googleapis.com/auth/gmail.settings.basic",
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/gmail.modify",
  "https://www.googleapis.com/auth/gmail.compose",
  "https://www.googleapis.com/auth/admin.directory.device.chromeos",
  "https://www.googleapis.com/auth/admin.directory.device.mobile",
  "https://www.googleapis.com/auth/ediscovery",
  "https://www.googleapis.com/auth/admin.reports.audit.readonly",
  "https://www.googleapis.com/auth/admin.reports.usage.readonly",
  "https://www.googleapis.com/auth/apps.alerts",
  "https://www.googleapis.com/auth/drive",
  "https://www.googleapis.com/auth/chat",
  "https://www.googleapis.com/auth/chat.spaces",
];

/** Comma-separated OAuth scopes for copy-to-clipboard in domain-wide delegation setup. */
export const gworkspaceOAuthScopesCsv = gworkspaceOAuthScopes.join(",");

/** Direct Admin console link for domain-wide delegation (Step 2). */
export const GWORKSPACE_DOMAIN_WIDE_DELEGATION_URL =
  "https://admin.google.com/ac/owl/domainwidedelegation";

export const appConfigSchemas: Record<string, ConfigField[]> = {
  m365: [
    { key: "tenant_id", label: "Tenant ID", type: "text", required: true },
    { key: "client_id", label: "Client ID", type: "text", required: true },
    {
      key: "client_secret",
      label: "Client Secret",
      type: "password",
      required: true,
      helpText:
        "To rotate an expired secret, paste a new value from Entra ID → App registrations → your app → Certificates & secrets, then click Test. Theo saves it after the credentials check succeeds.",
    },
  ],
  slack: [
    { key: "token_key", label: "Bot Token", type: "password", required: true },
  ],
  onepassword: [
    {
      key: "base_url",
      label: "Sign-in address",
      type: "text",
      required: true,
      placeholder: "https://yourteam.1password.com",
      helpText:
        "Your team's own 1Password address, as shown under \"Sign-in address\" in its Emergency Kit or in the address bar once signed in. Theo opens 1Password here.",
    },
  ],
  teams: [
    { key: "client_id", label: "Client ID", type: "text", required: true },
    { key: "client_secret", label: "Client Secret", type: "password", required: true },
    { key: "tenant_id", label: "Tenant ID", type: "text", required: true },
  ],
  autotask: [
    { key: "api_key", label: "API Key", type: "password", required: true },
    { key: "company_id", label: "Company ID", type: "text", required: true },
    { key: "webservice_url", label: "Webservice URL", type: "text", required: true },
  ],
  gworkspace: [
    {
      key: "service_account_json_upload",
      label: "Service Account JSON",
      type: "file",
      required: true,
      accept: ".json,application/json",
      fileReadMode: "text",
      uploadLabel: "Choose service-account JSON",
      configuredLabel: "JSON configured",
      helpText:
        "Upload the JSON key file downloaded from Google Cloud Console (Step 1 in the setup guide).",
    },
    {
      key: "admin_email",
      label: "Super Admin Email",
      type: "email",
      required: true,
      placeholder: "sandbox-admin@clientdomain.com",
      helpText:
        "Full email address of a Super Admin in the domain. Used for API impersonation — no password is needed.",
    },
  ],
};

export const platformConfigSchemas: Record<string, ConfigField[]> = {
  autotask: [
    { key: "base_url", label: "Base URL", type: "text", required: true, placeholder: "https://webservices2.autotask.net/atservicesrest" },
    { key: "username", label: "Username", type: "text", required: true },
    { key: "password", label: "Password", type: "password", required: true },
    { key: "api_integration_code", label: "API Integration Code", type: "text", required: true },
  ],
  superops: [
    { key: "graphql_url", label: "GraphQL URL", type: "text", required: true, placeholder: "https://api.superops.ai/msp" },
    { key: "subdomain", label: "Subdomain", type: "text", required: true, placeholder: "your-subdomain" },
    { key: "token_key", label: "API Token", type: "password", required: true },
  ],
  halo: [
    {
      key: "url",
      label: "URL",
      type: "text",
      required: true,
      placeholder: "https://your-instance.halopsa.com",
      helpText: "Full instance URL.",
    },
    { key: "client_id", label: "Client ID", type: "text", required: true },
    { key: "client_secret", label: "Client Secret", type: "password", required: true },
  ],
  ninjaone: [
    { key: "instance_url", label: "Instance URL", type: "text", required: true, placeholder: "https://us2.ninjarmm.com" },
    {
      key: "client_id",
      label: "Client ID",
      type: "text",
      required: true,
      helpText: "Client ID from the NinjaOne Web client app.",
    },
    {
      key: "client_secret",
      label: "Client Secret",
      type: "password",
      required: true,
      helpText: "Client secret from the same NinjaOne Web client app.",
    },
    {
      key: "refresh_token",
      label: "Refresh Token",
      type: "password",
      required: true,
      helpText: "Use Get Refresh Token after entering the Web client app details, or paste an existing Refresh Token.",
    },
  ],
  connectwise: [
    {
      key: "base_url",
      label: "Base URL",
      type: "text",
      required: true,
      placeholder: "https://your-connectwise-host",
      helpText: "ConnectWise instance root URL.",
    },
    {
      key: "company_id",
      label: "Company ID",
      type: "text",
      required: true,
      helpText: "Same Company ID used on the ConnectWise login screen.",
    },
    {
      key: "public_key",
      label: "Public Key",
      type: "text",
      required: true,
      helpText: "Generated from the API member's API Keys tab in ConnectWise PSA.",
    },
    {
      key: "private_key",
      label: "Private Key",
      type: "password",
      required: true,
      helpText: "Only shown once when the API key pair is created. Create a new pair if you lost it.",
    },
  ],
  nable: [
    { key: "base_url", label: "Base URL", type: "text", required: true, placeholder: "https://ncod180.n-able.com" },
    { key: "api_key", label: "API Key (JWT Token)", type: "password", required: true },
  ],
  datto: [
    { key: "api_url", label: "API URL", type: "text", required: true, placeholder: "https://vidal-api.centrastage.net" },
    { key: "api_key", label: "API Key", type: "text", required: true },
    { key: "api_secret_key", label: "API Secret Key", type: "password", required: true },
  ],
  itglue: [
    {
      key: "base_url",
      label: "Base URL",
      type: "text",
      required: true,
      placeholder: IT_GLUE_DEFAULT_BASE_URL,
    },
    { key: "api_key", label: "API Key", type: "password", required: true },
  ],
  hudu: [
    { key: "base_url", label: "Base URL", type: "text", required: true, placeholder: "https://your-domain.huducloud.com/api/v1" },
    { key: "api_key", label: "API Key", type: "password", required: true },
  ],
  sherweb: [
    {
      key: "gateway_base_url",
      label: "Base URL",
      type: "text",
      required: false,
      placeholder: SHERWEB_DEFAULT_GATEWAY_BASE_URL,
      helpText: "Defaults to the public Sherweb API URL.",
    },
    { key: "client_id", label: "Client ID", type: "text", required: true },
    { key: "client_secret", label: "Client Secret", type: "password", required: true },
    {
      key: "subscription_key",
      label: "Subscription Key",
      type: "password",
      required: true,
      helpText: "Generated in the Sherweb Partner Portal with the API application.",
    },
  ],
};

/** Display / DB platform name → `platformConfigSchemas` key (slugify alone can yield e.g. `it-glue`). */
const PLATFORM_CONFIG_SCHEMA_SLUG_ALIASES: Record<string, string> = {
  "it-glue": "itglue",
};

/**
 * Resolve config form fields for a platform by its stored/display name.
 * Uses only name + `platformConfigSchemas` — no `platform.type` fallback.
 */
export function getPlatformConfigSchemaFields(platformName: string): ConfigField[] {
  const slug = slugify(platformName);
  const key = PLATFORM_CONFIG_SCHEMA_SLUG_ALIASES[slug] ?? slug;
  return platformConfigSchemas[key] ?? [];
}

// Available platforms with their supported types
export const availablePlatforms = [
  { name: 'autotask', types: ['psa', 'rmm'], description: 'Autotask PSA/RMM platform' },
  { name: 'superops', types: ['psa', 'rmm'], description: 'SuperOps PSA/RMM platform' },
  { name: 'halo', types: ['psa'], description: 'Halo PSA platform' },
  { name: 'ninjaone', types: ['psa', 'rmm'], description: 'NinjaOne PSA/RMM platform' },
  { name: 'connectwise', types: ['psa'], description: 'ConnectWise PSA platform' },
  { name: 'nable', types: ['rmm'], description: 'N-able RMM platform' },
  { name: 'datto', types: ['rmm'], description: 'Datto RMM platform' },
  { name: 'itglue', types: ['docs'], description: 'ITGlue Documentation platform' },
  { name: 'hudu', types: ['docs'], description: 'Hudu Documentation platform' },
  { name: 'sherweb', types: ['marketplace'], description: 'Sherweb Marketplace platform' },
];

// Available applications
export const availableApplications = [
  { name: 'm365', description: 'Microsoft 365 integration' },
  { name: 'gworkspace', description: 'Google Workspace integration' },
  { name: 'slack', description: 'Slack integration' },
  { name: 'onepassword', description: '1Password integration' },
];

// AI Thread Observer access is gated by Horizon `user.role === 'ROOT'` (client + server), not email patterns.

/** RAG observer: `kb_documents.source_doc_type` options for query + cleanup. */
export const KB_RAG_SOURCE_DOC_TYPES: { value: string; label: string }[] = [
  { value: "kb", label: "kb" },
  { value: "guide", label: "guide" },
  { value: "hudu_articles", label: "hudu_articles" },
  { value: "itglue_docs", label: "itglue_docs" },
];
